/**
 * /admin/propagate – "Levitä oletukset aktiivisille lisensseille"
 *
 * Konservatiivinen päivitysten levitys: lisää PUUTTUVAT oletukset kaikille
 * aktiivisille lisensiasiakkaille EI KOSKAAN korvaa heidän omia muokkauksia.
 *
 * Käyttötapaus:
 * - Admin tekee koodimuutoksen jolla tulee uusia oletuskategorioita / asetuksia
 * - Admin avaa tämän näkymän, valitsee moduulit ja kohdetilit
 * - Esikatselu näyttää montako riviä lisätään per käyttäjä
 * - "Aloita levitys" → idempotent, ei korvaa olemassa olevaa
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, ShieldCheck, RotateCw, CheckCircle2, Info, Users } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function PropagateAdmin() {
  const [targets, setTargets] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState({}); // user_id → bool
  const [includeAdmin, setIncludeAdmin] = useState(true);
  const [includeLicenses, setIncludeLicenses] = useState(true);
  const [includeStaff, setIncludeStaff] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/propagate/targets");
      setTargets(r.data.targets || []);
      setAvailableModules(r.data.available_modules || []);
      setSelectedModules(r.data.available_modules || []); // oletus: kaikki
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleModule = (mod) => {
    setSelectedModules((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]
    );
  };
  const toggleTarget = (uid) =>
    setSelectedTargets((s) => ({ ...s, [uid]: !s[uid] }));

  const visibleTargets = targets.filter((t) => {
    if (!includeAdmin && t.role === "admin") return false;
    if (!includeLicenses && t.role === "license_customer") return false;
    if (!includeStaff && t.role === "staff") return false;
    return true;
  });

  const selectedIds = Object.entries(selectedTargets).filter(([, v]) => v).map(([k]) => k);

  const doPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const r = await api.post("/admin/propagate/preview", {
        modules: selectedModules,
        target_user_ids: selectedIds, // tyhjä = kaikki
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setPreviewing(false);
  };

  const doRun = async () => {
    if (selectedModules.length === 0) {
      toast.error("Valitse vähintään yksi moduuli");
      return;
    }
    const confirmMsg = `Levitetään oletukset valitusta moduulista ${visibleTargets.length} käyttäjälle. Ei voi korvata olemassa olevaa dataa. Jatketaanko?`;
    if (!window.confirm(confirmMsg)) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/propagate/run", {
        modules: selectedModules,
        target_user_ids: selectedIds,
        include_admin: includeAdmin,
        include_licenses: includeLicenses,
        include_staff: includeStaff,
      });
      setLastResult(r.data);
      toast.success(`✓ ${r.data.total_added} oletusta lisätty ${r.data.total_users} käyttäjälle`);
      // Päivitä esikatselu
      doPreview();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setRunning(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/40">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="propagate-admin">
      <Toaster richColors position="top-center" />

      <header className="space-y-1">
        <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-300/80">Pääkäyttäjän työkalu</p>
        <h1 className="text-3xl font-display tracking-tight flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-emerald-300" />
          Levitä oletukset
        </h1>
        <p className="text-sm text-white/60 max-w-2xl">
          Kun teet koodimuutoksen, joka lisää uusia OLETUSARVOJA (esim. ravintolakategoriat,
          tilinumerot, palkka-asetukset), tämä työkalu levittää ne kaikille aktiivisille
          lisenssiasiakkaille. <strong className="text-emerald-300">Ei koskaan korvaa</strong>{" "}
          asiakkaan omia muokkauksia – lisää vain puuttuvat rivit.
        </p>
      </header>

      {/* Turvavaroitus */}
      <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4 flex gap-3" data-testid="safety-banner">
        <ShieldCheck className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
        <div className="text-xs space-y-1">
          <p className="text-emerald-200 font-medium">Turvallinen levitys</p>
          <ul className="text-white/70 space-y-0.5 list-disc list-inside">
            <li>Idempotentti: voit ajaa useita kertoja ilman tuplauksia</li>
            <li>Owner-eristetty: vain valittujen käyttäjien data muuttuu</li>
            <li>Audit-logiin tallennetaan tarkasti mitä lisättiin</li>
          </ul>
        </div>
      </div>

      {/* Asetukset */}
      <div className="glass-card p-5 space-y-4">
        <p className="text-xs uppercase tracking-widest text-emerald-300/80">Asetukset</p>

        {/* Moduulit */}
        <div>
          <Label className="text-xs mb-2">Levitettävät moduulit</Label>
          <div className="flex flex-wrap gap-2" data-testid="module-chips">
            {availableModules.map((mod) => {
              const active = selectedModules.includes(mod);
              return (
                <button
                  key={mod}
                  onClick={() => toggleModule(mod)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition ${
                    active
                      ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
                      : "bg-white/[0.02] border-white/10 text-white/60"
                  }`}
                  data-testid={`module-chip-${mod}`}
                >
                  {mod} {active && <CheckCircle2 className="inline w-3 h-3 ml-1" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Kohderyhmät */}
        <div>
          <Label className="text-xs mb-2">Kohderyhmät</Label>
          <div className="grid grid-cols-3 gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={includeAdmin} onCheckedChange={setIncludeAdmin} data-testid="toggle-admin" />
              <span className="text-xs">Admin-tili (sinä)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={includeLicenses} onCheckedChange={setIncludeLicenses} data-testid="toggle-licenses" />
              <span className="text-xs">Lisenssiasiakkaat</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={includeStaff} onCheckedChange={setIncludeStaff} data-testid="toggle-staff" />
              <span className="text-xs">Henkilökunta</span>
            </label>
          </div>
        </div>

        {/* Käyttäjälista (esikatselu) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">Kohdekäyttäjät ({visibleTargets.length})</Label>
            <p className="text-[10px] text-white/40">
              {selectedIds.length === 0 ? "Tyhjä valinta = kaikki suodatetut" : `${selectedIds.length} valittu`}
            </p>
          </div>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-white/10 bg-white/[0.02]" data-testid="targets-list">
            {visibleTargets.length === 0 ? (
              <p className="text-xs text-white/40 p-4 text-center italic">Ei kohteita suodattimella</p>
            ) : (
              visibleTargets.map((t) => (
                <label
                  key={t.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-white/[0.03] cursor-pointer border-b border-white/5 last:border-b-0"
                  data-testid={`target-${t.id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!selectedTargets[t.id]}
                    onChange={() => toggleTarget(t.id)}
                  />
                  <span className="text-xs text-white/80 flex-1 min-w-0 truncate">
                    {t.full_name} <span className="text-white/40">· {t.email}</span>
                  </span>
                  <span className="text-[10px] text-emerald-300 uppercase tracking-wider">{t.role}</span>
                  {t.is_current_admin && <span className="text-[10px] text-amber-300">(sinä)</span>}
                </label>
              ))
            )}
          </div>
        </div>

        {/* Toiminnot */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            onClick={doPreview}
            disabled={previewing || selectedModules.length === 0}
            className="border-cyan-400/30 text-cyan-200 inline-flex items-center gap-2"
            data-testid="preview-button"
          >
            {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Info className="w-3.5 h-3.5" />}
            Esikatsele
          </Button>
          <Button
            onClick={doRun}
            disabled={running || selectedModules.length === 0}
            className="btn-luxe inline-flex items-center gap-2"
            data-testid="run-button"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
            Aloita levitys
          </Button>
        </div>
      </div>

      {/* Preview tulos */}
      {preview && (
        <div className="glass-card p-5 space-y-3" data-testid="preview-result">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-cyan-300/80">Esikatselu</p>
            <p className="text-xs text-white/60">{preview.total_users} käyttäjää</p>
          </div>
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.03] text-white/50">
                <tr>
                  <th className="text-left px-3 py-2">Käyttäjä</th>
                  <th className="text-right px-3 py-2">Rooli</th>
                  {preview.modules.map((m) => (
                    <th key={m} className="text-right px-3 py-2">{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((p) => (
                  <tr key={p.user_id} className="border-t border-white/5">
                    <td className="px-3 py-2 text-white/80">{p.email}</td>
                    <td className="text-right px-3 py-2 text-white/40">{p.role}</td>
                    {preview.modules.map((m) => {
                      const stat = p.modules[m] || {};
                      return (
                        <td key={m} className="text-right px-3 py-2 font-mono">
                          {stat.would_add > 0 ? (
                            <span className="text-emerald-300">+{stat.would_add}</span>
                          ) : (
                            <span className="text-white/30">0</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Last run tulos */}
      {lastResult && (
        <div className="glass-card p-5 space-y-3 border border-emerald-500/20" data-testid="last-result">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-emerald-300/80">Edellinen levitys</p>
            <div className="text-xs text-emerald-300 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {lastResult.total_added} lisätty / {lastResult.total_users} käyttäjää
            </div>
          </div>
          <div className="text-xs text-white/60">
            <Users className="inline w-3 h-3 mr-1" />
            Moduulit: {lastResult.modules?.join(", ")}
          </div>
        </div>
      )}
    </div>
  );
}
